import {
    maxRule,
    minRule,
    Popover,
    requiredRule,
    useToastContext,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { getLoanDetailInfo, putLoanDetailInfo } from 'axios/orderApi';
import { datetimeWithSecondsFormat } from 'constans/dateFormat';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { LoanDetailInfo } from 'models/api/order';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { FormGroup } from 'reactstrap';

const EditPopover = ({
    statusCode,
    isLoanEditPossible,
    id,
}: {
    statusCode: string;
    isLoanEditPossible: boolean;
    id: number;
}) => {
    const api = useApi();
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const { addToast } = useToastContext();
    const { addSubscription, removeSubscription } = useNotifyContext();
    const queryClient = useQueryClient();

    const [wasOpened, setWasOpened] = useState(false);
    const [loanInfo, setLoanInfo] = useState<LoanDetailInfo | null>(null);

    const loanQuery = useQuery(
        [`loanDetailInfo${id}`, wasOpened],
        () => (wasOpened ? getLoanDetailInfo(api, { loanId: id }) : null),
        {
            onSuccess: (data) => setLoanInfo(data),
        }
    );

    useEffect(() => {
        addSubscription({
            key: 'onLoanRecreateFinish',
            type: 'LoanRecreateFinish',
            callback: (message) => {
                const res = JSON.parse(message);
                addToast({
                    type: 'info',
                    description:
                        res.Err ?? t('BackOfficeLoanRecreateSuccessfullyFinish', [res.LoanId]),
                });
                res.Err && setWasOpened((prev) => !prev);

                res.Err ?? queryClient.invalidateQueries('orderInfo');
            },
        });

        return () => {
            removeSubscription('onLoanRecreateFinish');
        };
    }, []);

    return statusCode !== 'cancel' && isLoanEditPossible ? (
        <Popover<LoanDetailInfo>
            label="edit"
            textStyle={'warning'}
            buttonIcon={faWrench}
            buttonLabel={'BackOfficeEdit'}
            refetchQueryKeys={null}
            header={
                <>
                    <Trans i18nKey={'BackOfficeEdit'} />
                    {` #${id}`}
                </>
            }
            mutationFunction={(data) => {
                if (loanInfo) return putLoanDetailInfo(api, { ...loanInfo, ...data });
                else throw new Error('LoanDetail is empty');
            }}
            onToggleCallback={() => setWasOpened((prev) => !prev)}
            defaultValues={{
                loanTerm: loanInfo?.loanTerm,
                loanAmount: loanInfo?.loanAmount,
                loanIssueDate: loanInfo?.loanIssueDate,
                dsc: loanInfo?.dsc,
            }}
            canSave={hasRole('orderPutLoanDetailInfo') && !loanQuery.isFetching}
        >
            <ValidatedInput<LoanDetailInfo>
                name="loanTerm"
                type="number"
                loading={loanQuery.isFetching}
                label={t('BackOfficeLoanTermsTerm')}
                rules={{
                    max: maxRule(loanInfo?.loanTermMax),
                    min: minRule(loanInfo?.loanTermMin),
                }}
            />
            <ValidatedInput<LoanDetailInfo>
                name="loanAmount"
                type="number"
                loading={loanQuery.isFetching}
                label={t('BackOfficeLoanAmount')}
                rules={{
                    required: requiredRule,
                    max: maxRule(loanInfo?.loanMaxSum),
                    min: minRule(loanInfo?.loanMinSum),
                }}
            />
            <FormGroup>
                <BOValidatedDatePicker<LoanDetailInfo>
                    name="loanIssueDate"
                    inputProps={{
                        label: t('BackOfficeLoanIssueDate'),
                        loading: loanQuery.isFetching,
                    }}
                    dateFormat={datetimeWithSecondsFormat}
                    rules={{
                        required: requiredRule,
                    }}
                />
            </FormGroup>
            <ValidatedInput<LoanDetailInfo>
                name="dsc"
                type="textarea"
                loading={loanQuery.isFetching}
                label={t('BackOfficeComment')}
                rules={{
                    required: requiredRule,
                }}
            />
        </Popover>
    ) : (
        <></>
    );
};

export default EditPopover;
